import React, { useState } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import Dropzone from 'react-dropzone';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';
import FileProgressBar from './FileProgressBar';
import './FileConverter.css';
import config from '../../config';

export default function FileConverter() {
  const [files, setFiles] = useState([]);

  const useStyles = makeStyles({
    chooseFilesBtn: {
      backgroundColor: '#757575',
      color: '#FFFFFF',
    },
    clearAllBtn: {
      backgroundColor: '#00000099',
      color: '#00000099',
    },
    downloadAllBtnActive: {
      backgroundColor: '#2B6ED3',
      color: '#FFFFFF',
    },
    downloadAllBtnDisabled: {
      backgroundColor: '#0000001F',
      color: '#00000042',
    },
  });

  function uploadFitFile(file) {
    return new Promise((resolve, reject) => {
      const id = file.name;
      const progress = 0;
      const status = 'uploading';
      const fileObj = {
        id,
        file,
        progress,
        status,
      };

      const fileReader = new FileReader();
      fileReader.onload = function handleFileLoad() {
        const fileData = fileReader.result;

        const blob = new Blob([fileData], { type: 'application/octet-stream' });

        const uploadConfig = {
          headers: {
            'Content-Type': 'application/octet-stream',
            'X-File-Name': file.name,
            'X-API-KEY': config.convertApiKey,
          },
          onUploadProgress: (progressEvent) => {
            const progressValue = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const updatedFileObj = {
              ...fileObj,
              progress: progressValue,
            };
            setFiles((prevFiles) => prevFiles.map((prevFile) => (prevFile.id === updatedFileObj.id ? updatedFileObj : prevFile)));
          },
        };

        axios
          .post(config.convertUrl, blob, uploadConfig)
          .then((response) => {
            const url = response.data.downloadLink;
            const result = { ...fileObj, status: 'success', url };
            setFiles((prevFiles) => prevFiles.map((prevFile) => (prevFile.id === result.id ? result : prevFile)));
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      };

      fileReader.readAsArrayBuffer(file);
    });
  }

  const handleDrop = (acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) => {
      const id = file.name;
      const progress = 0;
      const status = 'uploading';
      const fileObj = {
        id,
        file,
        progress,
        status,
      };

      if (!file.name.toLowerCase().endsWith('.fit')) {
        fileObj.status = 'error';
        return fileObj;
      }

      uploadFitFile(file)
        .then((result) => {
          setFiles((prevFiles) => prevFiles.map((prevFile) => (prevFile.id === result.id ? result : prevFile)));
        })
        .catch(() => {
          // do nothing
        });

      return fileObj;
    });
    setFiles((prevFiles) => prevFiles.concat(newFiles));
  };

  const handleRemove = (index) => {
    if (typeof index !== 'undefined') {
      const newFiles = [...files];
      newFiles.splice(index, 1);
      setFiles(newFiles);
    } else {
      setFiles([]);
    }
  };

  const handleDownloadAll = () => {
    const successFiles = files.filter((file) => file.status === 'success' && file.url);

    if (successFiles.length === 0) {
      return;
    }

    const zip = new JSZip();

    const downloadPromises = successFiles.map((file) => axios
      .get(file.url, { responseType: 'text' })
      .then((response) => {
        const fileName = file.file.name.replace('.fit', '.csv');
        zip.file(fileName, response.data);
      })
      .catch(() => {
        // do nothing
      }));

    Promise.all(downloadPromises)
      .then(() => {
        zip.generateAsync({ type: 'blob' }).then((content) => {
          const downloadUrl = URL.createObjectURL(content);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = 'converted_csv_files.zip';
          link.click();
        });
      })
      .catch(() => {
        // do nothing
      });
  };

  const canSubmit = files.length > 0 && files.every((file) => file.status === 'success' || file.status === 'error');

  return (
    <div className="file-converter-container">
      <div className="file-converter-header">
        <div className="file-converter-header-text">
          <span className="file-converter-header-title">Convert FIT to CSV</span>
          <p className="file-converter-header-subtitle">
            FIT files contain various data (distance, time, location, calories, heart rate, speed, etc.) and can be converted to CSV for use in
            spreadsheets.
          </p>
        </div>
        <div className="file-converter-header-buttons">
          <Button
            variant="outlined"
            className={useStyles.clearAllBtn}
            onClick={() => handleRemove()}
            startIcon={<CloseIcon />}
            style={{ textTransform: 'none' }}
          >
            Clear All
          </Button>
          {canSubmit ? (
            <Button
              variant="contained"
              className={useStyles.downloadAllBtnActive}
              onClick={() => handleDownloadAll()}
              startIcon={<DownloadIcon />}
              style={{ textTransform: 'none' }}
            >
              Download All
            </Button>
          ) : (
            <Button
              variant="outlined"
              className={useStyles.downloadAllBtnDisabled}
              disabled
              startIcon={<DownloadIcon />}
              style={{ textTransform: 'none' }}
            >
              Download All
            </Button>
          )}
        </div>
      </div>
      <div className="file-converter-content">
        <div className="dropzone">
          <Dropzone onDrop={handleDrop}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <div className="dropzone-words">
                  <h3 className="dropzone-title">Choose Files to Upload</h3>
                  <p className="dropzone-description">Select multiple by holding Shift for adjacent or Ctrl for non-adjacent files.</p>
                  <div className="mui-button-container">
                    <Button variant="contained" className={useStyles.chooseFilesBtn} startIcon={<UploadIcon />} style={{ textTransform: 'none' }}>
                      Choose Files
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Dropzone>
        </div>
        <div className="file-converter-header-buttons-small">
          <Button
            variant="outlined"
            className={useStyles.clearAllBtn}
            onClick={() => handleRemove()}
            startIcon={<CloseIcon />}
            style={{ textTransform: 'none' }}
          >
            Clear All
          </Button>
          {canSubmit ? (
            <Button
              variant="contained"
              className={useStyles.downloadAllBtnActive}
              onClick={() => handleDownloadAll()}
              startIcon={<DownloadIcon />}
              style={{ textTransform: 'none' }}
            >
              Download All
            </Button>
          ) : (
            <Button
              variant="outlined"
              className={useStyles.downloadAllBtnDisabled}
              disabled
              startIcon={<DownloadIcon />}
              style={{ textTransform: 'none' }}
            >
              Download All
            </Button>
          )}
        </div>
        <div
          className="file-list-container"
          style={{
            backgroundImage: files.length > 0 ? 'none' : `url(${process.env.PUBLIC_URL}/file-converter-background.svg)`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          {files.length > 0 && (
            <div>
              <table className="file-list">
                <tbody>
                  {files.map((fileObj, index) => (
                    <tr key={`file-${fileObj.id}`}>
                      <td>
                        <InsertDriveFileIcon
                          style={{
                            color: fileObj.status === 'uploading' ? '#E0E0E0' : '#757575',
                            verticalAlign: 'text-bottom',
                          }}
                        />
                      </td>
                      <td className="file-details">
                        <div className="file-details-row">
                          <div className={`file-name ${fileObj.status === 'uploading' ? 'file-status-uploading' : 'file-status-success'}`}>
                            {fileObj.file.name}
                          </div>
                          <div className="file-status">
                            {fileObj.status === 'uploading' ? (
                              <span className="file-status-uploading">Converting file...</span>
                            ) : (
                              <div>
                                {fileObj.status === 'success' && (
                                  <div className="file-success-message">
                                    <CheckIcon style={{ color: '#4CAF50', verticalAlign: 'text-bottom' }} />
                                    <span className="converted">File Converted</span>
                                  </div>
                                )}
                                {fileObj.status === 'error' && (
                                  <div className="file-error-message">
                                    <ErrorOutlineIcon style={{ color: '#F44336', verticalAlign: 'text-bottom' }} />
                                    <span>Upload error: invalid file format</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={fileObj.status === 'uploading' ? 'file-progress-bar' : 'file-progress-bar-complete'}>
                          <FileProgressBar progress={fileObj.progress} />
                        </div>
                      </td>
                      <td>
                        <div className="file-buttons">
                          <a href={fileObj.status === 'uploading' ? null : fileObj.url} download>
                            <DownloadIcon
                              style={{
                                color: fileObj.status === 'uploading' ? '#00000042' : '#0000008A',
                                verticalAlign: 'text-bottom',
                              }}
                            />
                          </a>
                          <CancelIcon
                            onClick={() => handleRemove(index)}
                            style={{
                              color: '#0000008A',
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
